import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import debounce from 'lodash/debounce';
import HeidelbergBook from './HeidelbergBook';
import ScrollingBook from './ScrollingBook';
import CharacterPicker from '../CharacterPicker';
import BreadCrumb from '../BreadCrumb';
import CanvasDrawerMessage from '../CanvasDrawers/CanvasDrawerMessage';
import {
  pagesPropTypes,
  parseQuery,
  isMediaQueriesDifferent,
  getUpdatedPages,
  getUpdatedPreviewOptions,
} from './helpers';
import { getTransform } from '../CharacterPicker/helpers';
import Loading from '../Loading';
import { isTouchEnabled } from 'src/common/utils/touch';
import './BookPreview.scss';

class BookPreview extends React.Component {
  static propTypes = {
    productKey: PropTypes.string.isRequired,
    productId: PropTypes.string.isRequired,
    transform: PropTypes.string.isRequired,
    breadcrumbCTA: PropTypes.string.isRequired,
    completedDrawer: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
    hasInsideCovers: PropTypes.bool.isRequired,
    isHeidelberg: PropTypes.bool.isRequired,
    needsTransform: PropTypes.bool.isRequired,
    hasBreadCrumb: PropTypes.bool.isRequired,
    sortBreadcrumbs: PropTypes.bool.isRequired,
    hasCharacterPicker: PropTypes.bool,
    padding: PropTypes.number.isRequired,
    activePage: PropTypes.number.isRequired,
    imageWidth: PropTypes.number.isRequired,
    scrollPosition: PropTypes.number.isRequired,
    multiplier: PropTypes.number,
    productCustomisation: PropTypes.object.isRequired,
    media: PropTypes.shape({
      desktopUp: PropTypes.bool,
    }).isRequired,
    query: PropTypes.shape({
      page: PropTypes.string.isRequired,
    }).isRequired,
    history: PropTypes.shape({
      listen: PropTypes.func.isRequired,
    }).isRequired,
    completedDrawersMap: PropTypes.shape({
      incomplete: PropTypes.array.isRequired,
    }).isRequired,
    drawers: PropTypes.shape({
      other: PropTypes.array.isRequired,
    }).isRequired,
    prismicProduct: PropTypes.shape({
      response: PropTypes.shape({
        ok: PropTypes.bool,
      }),
      canvas: PropTypes.shape({
        bookPages: PropTypes.array,
      }),
    }),
    pages: pagesPropTypes.isRequired,
    navItems: PropTypes.array.isRequired,
    breadcrumbs: PropTypes.array,
    covers: PropTypes.array,
    setActivePage: PropTypes.func.isRequired,
    updateBookPreviewPages: PropTypes.func.isRequired,
    setIsHeidelberg: PropTypes.func.isRequired,
    buildPreviewPages: PropTypes.func.isRequired,
    sendPreviewFailure: PropTypes.func.isRequired,
    updateProductCustomisation: PropTypes.func.isRequired,
    setScrollPosition: PropTypes.func.isRequired,
    calculatePreviewImageWidth: PropTypes.func,
    setNeedsTransform: PropTypes.func,
    setTransform: PropTypes.func,
  };

  handleResize = () => {
    this.props.calculatePreviewImageWidth();
    if (window.innerWidth < this.props.navItems.length * 50) {
      this.props.setNeedsTransform(true);
      const transform = getTransform(this.props.navItems, 0, window.innerWidth);
      this.props.setTransform(transform);
    } else {
      this.props.setNeedsTransform(false);
    }
  };

  _debouncedResize = debounce(this.handleResize, 250);

  componentDidMount() {
    this._debouncedResize();
    window.addEventListener('resize', this._debouncedResize);

    const props = this.props;
    props.setIsHeidelberg(!isTouchEnabled() || !!props.media.desktopUp);
    props.calculatePreviewImageWidth();

    if (props.query.page) {
      props.setActivePage(parseInt(props.query.page, 10));
    }

    props.history.listen((location, method) => {
      if (method === 'POP') {
        const newProductCustomisation = parseQuery(location.search);
        props.updateProductCustomisation(newProductCustomisation);
      }
    });
  }

  componentDidUpdate(prevProps) {
    const props = this.props;

    this._debouncedResize();
    if (isMediaQueriesDifferent(prevProps, props)) {
      prevProps.setIsHeidelberg(!isTouchEnabled() || !!props.media.desktopUp);
      this._debouncedResize();
    }

    const updatedPages = getUpdatedPages(prevProps, props);
    if (updatedPages) {
      props.updateBookPreviewPages(updatedPages);
    }

    let updatedPreviewOptions;
    try {
      updatedPreviewOptions = getUpdatedPreviewOptions(prevProps, props);
    } catch (error) {
      prevProps.sendPreviewFailure({
        details: {
          errors: [error.toString()]
        }
      });
    }

    if (updatedPreviewOptions) {
      prevProps.buildPreviewPages(updatedPreviewOptions);
    }
  }

  componentWillUnmount() {
    this._debouncedResize.cancel();
    window.removeEventListener('resize', this._debouncedResize);
  }

  render() {
    const props = this.props;
    const preview = props.isHeidelberg
      ? (
        <HeidelbergBook
          pages={props.pages}
          activePage={props.activePage}
          setActivePage={props.setActivePage}
        />
      )
      : (
        <ScrollingBook
          hasInsideCovers={props.hasInsideCovers}
          pages={props.pages}
          productId={props.productId}
          activePage={props.activePage}
          setActivePage={props.setActivePage}
          imageWidth={props.multiplier * 2 / 100 * props.imageWidth}
          containerWidth={props.imageWidth * (props.pages.length - 1)}
          scrollPosition={props.scrollPosition}
          setScrollPosition={props.setScrollPosition}
        />
      );
    return (
      <div
        className={cx('book-preview-wrapper', {
          'book-preview-wrapper--with-breadcrumb':
            props.hasCharacterPicker || props.hasBreadCrumb,
          'container-guttered': props.isHeidelberg,
        })}>
        {props.hasCharacterPicker && (
          <CharacterPicker
            pages={props.pages}
            updateBookPreviewPages={props.updateBookPreviewPages}
            activePage={props.activePage}
            setActivePage={props.setActivePage}
            items={props.navItems}
            isHeidelberg={props.isHeidelberg}
            needsTransform={props.needsTransform}
            setTransform={props.setTransform}
            transform={props.transform}
          />
        )}
        {props.hasBreadCrumb && (
          <BreadCrumb
            items={props.breadcrumbs}
            sortByPageNumber={props.sortBreadcrumbs}
            activePage={props.activePage}
            setActivePage={props.setActivePage}
            breadcrumbCTA={props.breadcrumbCTA}
            completedDrawersMap={props.completedDrawersMap}
            drawers={props.drawers}
          />
        )}
        <div
          className={cx('book-preview', {
            'book-preview--heidelberg': props.isHeidelberg,
          })}
          style={{
            paddingTop: `${props.padding}%`,
            width: props.isHeidelberg ? `${props.multiplier * 2}%` : 'auto',
          }}>
          <CanvasDrawerMessage
            drawers={props.drawers.other}
            drawer={props.completedDrawer}
            completedDrawersMap={props.completedDrawersMap}
            stepsLeft={props.completedDrawersMap.incomplete.length}
          />
          {props.isLoading ? <Loading relative={!props.isHeidelberg} /> : preview}
        </div>
      </div>
    );
  }
}

export default BookPreview;



// WEBPACK FOOTER //
// ./src/client/js/view/components/BookPreview/BookPreview.js